'use client';

import { ArrowRight, Calendar, MapPin, Users } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { Carousel } from '@/components/ui/carousel';
import {
  useLandingEvents,
  type LandingEvent,
} from '@/hooks/use-landing-events';
import { useAnalytics } from '@/services/frontendAnalyticsService';
/** Content width shared with the surrounding landing sections. */
const SECTION_PADDING = 'mx-auto max-w-[1440px] px-6 lg:px-16 xl:px-24';

/** Compact card date (e.g. "12 Agu 2025"); ranges collapse to "start – end". */
function formatCardDate(event: LandingEvent): string {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: event.timezone,
  };
  const start = new Date(event.startAt).toLocaleDateString('id-ID', options);

  if (!event.endAt) return start;

  const end = new Date(event.endAt);
  if (new Date(event.startAt).toDateString() === end.toDateString()) {
    return start;
  }

  return `${start} – ${end.toLocaleDateString('id-ID', options)}`;
}

/** Human-readable location: "Online" for webinars, venue otherwise. */
function eventLocation(event: LandingEvent): string {
  if (event.eventMode === 'online') return 'Online';
  return [event.venueName, event.venueCity].filter(Boolean).join(', ');
}

/**
 * Speaker names condensed into one line so the card stays scannable:
 * "A & B" for small panels, "A +n lainnya" once the list grows.
 */
function SpeakerLine({ speakers }: { speakers: LandingEvent['speakers'] }) {
  if (speakers.length === 0) return null;

  const names = speakers.map((speaker) => speaker.name);
  const label =
    names.length <= 2
      ? names.join(' & ')
      : `${names[0]} +${names.length - 1} lainnya`;

  return (
    <p className="mt-3 flex items-center gap-1.5 text-xs text-neutral-500">
      <Users className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
      <span className="line-clamp-1">{label}</span>
    </p>
  );
}

/** Branded placeholder shown while an event has no image at all. */
function EventImagePlaceholder() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500">
      <span className="font-bold uppercase tracking-widest text-white/50 text-xs">
        Justisio
      </span>
    </div>
  );
}

/**
 * One slide of the events carousel: a vertical card mirroring the
 * /blog/event listing — banner on top, then phase label, title, date,
 * location, speakers and the "Lihat Detail" action. The whole card opens
 * the event detail page; click tracking stays with the parent section.
 */
function CarouselEventCard({
  event,
  onCardClick,
}: {
  event: LandingEvent;
  onCardClick: () => void;
}) {
  // Prefer the portrait banner so the card reads vertically like an Instagram
  // post; the thumbnail only serves as a fallback for events without a banner.
  const image = event.featuredImage ?? event.thumbnailImage;
  const href = `/blog/event/${event.slug}`;
  // Three labels: highlighted stays pinned, and any other event is "Selesai"
  // once its start time has passed, otherwise "Mendatang".
  const label = event.isHighlighted
    ? 'Event Unggulan'
    : new Date(event.startAt).getTime() < Date.now()
      ? 'Event Selesai'
      : 'Event Mendatang';

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-100 bg-white shadow-sm transition-shadow hover:shadow-md">
      <Link
        href={href}
        onClick={onCardClick}
        aria-label={event.title}
        className="relative block aspect-[4/5] overflow-hidden bg-neutral-100"
      >
        {image ? (
          // object-contain keeps each photo in its original vertical 4:5
          // aspect instead of cropping it into a different ratio.
          <Image
            src={image}
            alt={event.title}
            fill
            sizes="(max-width: 640px) 100vw, 33vw"
            className="object-contain p-2 transition-transform duration-500 ease-out group-hover:scale-[1.03]"
          />
        ) : (
          <EventImagePlaceholder />
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-600">
          {label}
        </span>
        <Link href={href} onClick={onCardClick}>
          <h3 className="mt-1.5 text-base font-bold leading-snug text-neutral-900 line-clamp-2 transition-colors group-hover:text-navy-700">
            {event.title}
          </h3>
        </Link>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
            {formatCardDate(event)}
          </span>
          <span className="inline-flex items-center gap-1.5 capitalize">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
            {eventLocation(event)}
          </span>
        </div>

        <SpeakerLine speakers={event.speakers} />

        <div className="mt-auto pt-4">
          <Link
            href={href}
            onClick={onCardClick}
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 px-4 py-2 text-xs font-bold text-neutral-900 transition-colors hover:border-neutral-300 hover:bg-neutral-50"
          >
            Lihat Detail
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
}

/** Row of pulse placeholders approximating the carousel while events load. */
function EventSectionSkeleton() {
  return (
    <section className="bg-white py-12 md:py-16 lg:py-20">
      <div className={SECTION_PADDING}>
        <div className="h-8 w-2/3 animate-pulse rounded bg-neutral-100 md:h-10 lg:w-1/2" />
        <div className="mt-8 grid animate-pulse grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 md:mt-10">
          {[0, 1, 2].map((index) => (
            <div key={index} className="flex flex-col gap-3">
              <div className="aspect-[4/5] w-full rounded-2xl bg-neutral-100" />
              <div className="h-5 w-3/4 rounded bg-neutral-100" />
              <div className="h-4 w-1/2 rounded bg-neutral-100" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Events band of the landing page: a future highlighted event pins first,
 * everything else sorts by most recent startAt first. The section is
 * omitted entirely when there are no events to show.
 */
export function EventSection() {
  const analytics = useAnalytics();
  const { events, isLoading } = useLandingEvents();

  if (isLoading) return <EventSectionSkeleton />;
  if (events.length === 0) return null;

  const trackClick = (event: LandingEvent) =>
    analytics.landingPage.eventClicked({
      title: event.title,
      slug: event.slug,
    });

  return (
    <section className="bg-white py-12 md:py-16 lg:py-20">
      <div className={SECTION_PADDING}>
        <h2 className="mx-auto max-w-4xl text-center text-fluid-2xl font-medium leading-[1.2] tracking-tight text-neutral-900 md:text-fluid-3xl lg:text-fluid-4xl">
          Kembangkan Kompetensi Anda Melalui Rangkaian{' '}
          <span className="font-bold">Event Justisio</span>
        </h2>

        <Carousel className="mt-8 md:mt-10" visibleCount={{ base: 1, sm: 2, lg: 3 }}>
          {events.map((event) => (
            <CarouselEventCard
              key={event.id}
              event={event}
              onCardClick={() => trackClick(event)}
            />
          ))}
        </Carousel>

        <Link
          href="/blog/event"
          className="group/link mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900 transition-colors hover:text-amber-600"
        >
          Jelajahi Event Lainnya
          <ArrowRight
            size={14}
            className="transition-transform group-hover/link:translate-x-1"
            aria-hidden="true"
          />
        </Link>
      </div>
    </section>
  );
}
